import React from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import ProductImageWithHover from "@/features/purchase-orders/components/ProductImageWithHover";
import type { SKURuleCategoryKey } from "../types";

interface MissingSkuEntry {
  sku: string;
  base: string;
  title?: string;
  stockLevel?: number;
  imageUrl?: string;
}

interface MissingGarmentsAlertProps {
  missingSkus: MissingSkuEntry[];
  onAssign: (entry: MissingSkuEntry, category: SKURuleCategoryKey) => void;
}

export const MissingGarmentsAlert: React.FC<MissingGarmentsAlertProps> = ({
  missingSkus,
  onAssign,
}) => {
  if (missingSkus.length === 0) return null;

  return (
    <Alert className="mb-6 border-red-200 bg-red-50">
      <AlertTriangle className="h-4 w-4 text-red-600" />
      <AlertDescription className="text-red-800">
        <div className="flex items-start justify-between">
          <div className="w-full">
            <div className="font-medium mb-2">
              {missingSkus.length} SKU{missingSkus.length > 1 ? 's are' : ' is'} missing cleaning rules
            </div>
            <div className="text-sm mb-3">
              These SKUs exist in your inventory but don't match any current cleaning rule. Assign them to a bucket below.
            </div>
            <div className="grid gap-3 mb-3">
              {missingSkus.map((entry) => (
                <div
                  key={entry.sku}
                  className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3 p-3 bg-white/60 rounded border border-red-300"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <ProductImageWithHover
                      imageUrl={entry.imageUrl}
                      productSku={entry.sku}
                      size="medium"
                    />
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold text-red-800">{entry.sku}</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-red-600">
                          Base segment: <Badge variant="destructive" className="ml-1">{entry.base}</Badge>
                        </span>
                        {entry.title && (
                          <span className="text-xs text-red-600/70 truncate">{entry.title}</span>
                        )}
                        {entry.stockLevel !== undefined && (
                          <span className="text-xs text-red-600/70">
                            Stock: {entry.stockLevel}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm text-red-700 whitespace-nowrap">Add to bucket:</span>
                    <Select onValueChange={(category) => onAssign(entry, category as SKURuleCategoryKey)}>
                      <SelectTrigger className="w-52 h-8 border-red-300 focus:border-red-500">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standardGarments">
                          <div className="flex flex-col">
                            <span className="font-medium">Standard Garments</span>
                            <span className="text-xs text-muted-foreground">GARMENT-SIZE-COLOR</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="doubleSidedPrefixes">
                          <div className="flex flex-col">
                            <span className="font-medium">Double-Sided Prefixes</span>
                            <span className="text-xs text-muted-foreground">Adds "2" prefix, then standard rule</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="firstTwoSegments">
                          <div className="flex flex-col">
                            <span className="font-medium">First Two Segments</span>
                            <span className="text-xs text-muted-foreground">GARMENT-TYPE</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="wrapperTypes">
                          <div className="flex flex-col">
                            <span className="font-medium">Wrapper Types</span>
                            <span className="text-xs text-muted-foreground">Takes inner SKU</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="passThrough">
                          <div className="flex flex-col">
                            <span className="font-medium">Pass-Through</span>
                            <span className="text-xs text-muted-foreground">Returns unchanged</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-xs text-red-700">
              Until they are assigned, these SKUs will fall back to "return as-is" behavior when cleaned.
              PAP (pick-and-pack) SKUs are excluded: they are auto-matched by exact lookup during order sync.
            </div>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};
